// ── Shared weapon GLB loader for TPP/world surfaces (W6/W7) ──
// One process-wide cache shared by `tpp-weapon.ts` (third-person body weapon)
// and `weapon-pickup.ts` (dropped weapon entities). Both surfaces previously
// rendered as procedural boxes; this loader fetches the same GLBs the
// first-person viewmodel uses and caches scenes for cheap clones.

import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import type { WeaponId } from '../core/config';
import { WEAPON_MODELS } from './weapon-models';

let sharedLoader: GLTFLoader | null = null;
function getSharedLoader(): GLTFLoader {
  if (sharedLoader) return sharedLoader;
  sharedLoader = new GLTFLoader();
  const draco = new DRACOLoader();
  draco.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.7/');
  sharedLoader.setDRACOLoader(draco);
  return sharedLoader;
}

const sceneCache = new Map<WeaponId, THREE.Group>();
const inflight = new Map<WeaponId, Promise<THREE.Group | null>>();
const failed = new Set<WeaponId>();

/**
 * Load (or return cached) a clone of the weapon's GLB model. Returns null when
 * the weapon has no configured GLB or the GLB failed to load — callers fall
 * back to their procedural mesh in that case.
 */
export function loadWeaponWorldModel(weaponId: WeaponId): Promise<THREE.Group | null> {
  if (failed.has(weaponId)) return Promise.resolve(null);
  const cached = sceneCache.get(weaponId);
  if (cached) return Promise.resolve(cloneScene(cached));
  const pending = inflight.get(weaponId);
  if (pending) return pending.then((g) => (g ? cloneScene(g) : null));

  const cfg = WEAPON_MODELS[weaponId];
  if (!cfg) {
    failed.add(weaponId);
    return Promise.resolve(null);
  }

  // In Node (vitest `environment: 'node'`) there is no document.baseURI for
  // three's FileLoader to resolve a root-relative URL like `/models/...glb`
  // against. `new Request(path)` then throws `Invalid URL` synchronously and
  // surfaces as an unhandled rejection in CI. Callers already handle a null
  // GLB by keeping their procedural placeholder, so skip the fetch entirely.
  const hasDocumentBase = typeof document !== 'undefined' && !!document.baseURI;
  const isAbsoluteUrl = /^(https?:|file:|data:|blob:)/i.test(cfg.glbPath);
  if (!hasDocumentBase && !isAbsoluteUrl) {
    failed.add(weaponId);
    return Promise.resolve(null);
  }

  const promise = new Promise<THREE.Group | null>((resolve) => {
    getSharedLoader().load(
      cfg.glbPath,
      (gltf) => {
        const root = gltf.scene as THREE.Group;
        // Apply manifest transform once to the cached prototype so clones come
        // out positioned correctly. TPP/drop callers may scale or reposition
        // further on top of this.
        root.scale.setScalar(cfg.scale);
        root.position.set(...cfg.positionOffset);
        root.rotation.set(...cfg.rotationOffset);
        root.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            child.castShadow = true;
            child.receiveShadow = true;
          }
        });
        sceneCache.set(weaponId, root);
        inflight.delete(weaponId);
        resolve(root);
      },
      undefined,
      (err) => {
        console.warn(`[weapon-world-models] Failed to load ${weaponId} GLB:`, err);
        failed.add(weaponId);
        inflight.delete(weaponId);
        resolve(null);
      },
    );
  });
  inflight.set(weaponId, promise);
  return promise.then((g) => (g ? cloneScene(g) : null));
}

function cloneScene(src: THREE.Group): THREE.Group {
  // SkeletonUtils-free clone — weapons have no skinned meshes. Materials are
  // shared across clones to keep texture uploads cheap; geometry is shared too.
  const clone = src.clone(true);
  return clone;
}

/** Test/dev helper — wipe caches between hot-reloads. */
export function _resetWeaponWorldModelCacheForTests(): void {
  sceneCache.clear();
  inflight.clear();
  failed.clear();
}
